// Filtro de Cliente da Consulta: AUTOCOMPLETE que busca conforme o
// usuário digita (debounce de 300ms) usando o serviço de customers
// JÁ EXISTENTE do portal (GET /customers?name=|email=, o mesmo da
// página /customers). Selecionar um cliente preenche ?customerId na
// URL (URL como estado); o X limpa a seleção.
import { XIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useNotificationCenterNotificationsSearchParams } from "@/hooks"
import useCustomers from "@/hooks/use-customers"

// Mínimo de caracteres para disparar a busca (evita varreduras).
const CUSTOMER_SEARCH_MIN_CHARS = 3
const CUSTOMER_SEARCH_DEBOUNCE_MS = 300
const CUSTOMER_SEARCH_PAGE_SIZE = 10

const filtersPrefix =
  "pages.notificationCenter.searchPage.filters"

// Valor com atraso — só propaga depois de o usuário parar de digitar.
const useDebouncedValue = (
  value: string,
  delayMs: number
) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(
      () => setDebounced(value),
      delayMs
    )
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

// Nome exibível do cliente (fallback no e-mail e por fim no id).
const getCustomerDisplayName = (customer: Customer) => {
  const fullName = [customer.firstName, customer.lastName]
    .filter(Boolean)
    .join(" ")
    .trim()
  return fullName || customer.email || customer.id
}

export const NotificationSearchCustomerFilter = () => {
  const { t } = useTranslation()
  const { params, setParams } =
    useNotificationCenterNotificationsSearchParams()
  const [term, setTerm] = useState("")
  // Rótulo do cliente escolhido nesta sessão (a URL só guarda o id;
  // ao restaurar por deep-link exibimos o próprio CUS-…).
  const [selectedLabel, setSelectedLabel] = useState<
    string | undefined
  >(undefined)

  const debouncedTerm = useDebouncedValue(
    term,
    CUSTOMER_SEARCH_DEBOUNCE_MS
  ).trim()
  const canSearch =
    debouncedTerm.length >= CUSTOMER_SEARCH_MIN_CHARS

  // Termo com "@" busca por e-mail; senão por nome (query params do
  // finder-customers-by-filters do gateway).
  const customerFilters: CustomerSearchParams =
    debouncedTerm.includes("@")
      ? {
          email: debouncedTerm,
          page: 1,
          pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
        }
      : {
          name: debouncedTerm,
          page: 1,
          pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
        }
  const { data, isFetching } = useCustomers(
    customerFilters,
    canSearch
  )
  const customers = data?.customers ?? []

  const handleSelect = (customer: Customer) => {
    setSelectedLabel(getCustomerDisplayName(customer))
    setTerm("")
    setParams({ customerId: customer.id })
  }

  const handleClear = () => {
    setSelectedLabel(undefined)
    setTerm("")
    setParams({ customerId: undefined })
  }

  // Estado "selecionado": mostra o cliente escolhido + X para limpar.
  if (params.customerId) {
    return (
      <div className="flex h-10 items-center justify-between gap-2 rounded-md border bg-muted/40 px-3">
        <span className="truncate text-sm">
          {selectedLabel ?? params.customerId}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleClear}
          aria-label={t(`${filtersPrefix}.customerClear`)}
          className="size-6 shrink-0"
        >
          <XIcon className="size-4" aria-hidden="true" />
        </Button>
      </div>
    )
  }

  const showResults = canSearch && term.trim().length > 0

  return (
    <div className="relative">
      <Input
        id="notification-search-customer"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setTerm("")
        }}
        placeholder={t(
          `${filtersPrefix}.customerPlaceholder`
        )}
        autoComplete="off"
        aria-label={t(`${filtersPrefix}.customer`)}
      />
      {showResults && (
        <div className="absolute left-0 top-11 z-20 w-full min-w-64 rounded-md border bg-popover p-1 shadow-md">
          {isFetching && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {t(`${filtersPrefix}.customerSearching`)}
            </p>
          )}
          {!isFetching && customers.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {t(`${filtersPrefix}.customerNoResults`)}
            </p>
          )}
          {customers.length > 0 && (
            <ul
              role="listbox"
              aria-label={t(
                `${filtersPrefix}.customerResults`
              )}
              className="max-h-64 overflow-y-auto"
            >
              {customers.map((customer) => (
                <li key={customer.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => handleSelect(customer)}
                    className="flex w-full flex-col rounded-sm px-2 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                  >
                    <span className="text-sm">
                      {getCustomerDisplayName(customer)}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {[customer.email, customer.id]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
